import type {
  DictationFeedbackAnalyticsContext,
  DictationFeedbackApiRequest,
  DictationFeedbackApiResponse,
  DictationFeedbackSubmission,
} from '../../contracts';
import { getDictationAnalyticsPorts } from '../../dictation-analytics-ports';
import { trackFeedbackAnalyticsSafely } from '../analytics/trackFeedbackAnalyticsSafely';
import { t11nHttp } from './t11nHttp';

const FEEDBACK_REQUEST_TIMEOUT_MS = 10_000;

const requireDictationId = (dictationId: string | null): string => {
  if (!dictationId) {
    throw new Error('[dictation/feedback] cannot submit without a dictation id');
  }
  return dictationId;
};

export const toDictationFeedbackApiRequest = (
  submission: DictationFeedbackSubmission
): DictationFeedbackApiRequest => ({
  feedback: {
    rating: submission.score === 'positive' ? 'thumb_up' : 'thumb_down',
    reasons:
      submission.score === 'negative'
        ? submission.reasons.map((reason) => ({ reason }))
        : [],
    user_feedback:
      submission.score === 'negative' ? (submission.userFeedback ?? null) : null,
  },
});

/**
 * Writes to transcription-service first, then emits analytics for the saved
 * interaction. A failed request never produces a submitted event.
 */
export const submitDictationFeedback = async (
  submission: DictationFeedbackSubmission,
  analyticsContext: DictationFeedbackAnalyticsContext
): Promise<DictationFeedbackApiResponse> => {
  const dictationId = requireDictationId(submission.dictationId);
  const request = toDictationFeedbackApiRequest(submission);
  const { data } = await t11nHttp.put<DictationFeedbackApiResponse>(
    `/dictations/${encodeURIComponent(dictationId)}/feedback`,
    request,
    { timeout: FEEDBACK_REQUEST_TIMEOUT_MS }
  );

  trackFeedbackAnalyticsSafely('feedback_submitted', () => {
    getDictationAnalyticsPorts().feedback.trackSubmitted({
      dictation_id: dictationId,
      platform: submission.platform,
      surface: analyticsContext.surface,
      ...(analyticsContext.experimentVariant
        ? { experiment_variant: analyticsContext.experimentVariant }
        : {}),
      input_languages: analyticsContext.inputLanguages,
      output_languages: analyticsContext.outputLanguages,
      ...(analyticsContext.region ? { region: analyticsContext.region } : {}),
      feedback_state: submission.score,
      rating: request.feedback.rating,
      reasons: submission.score === 'negative' ? submission.reasons : [],
      has_feedback_text: Boolean(request.feedback.user_feedback),
    });
  });

  return data;
};
